import type { ReactNode } from "react";
import { Flex, Heading, Text } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import type { ImageResource } from "models/types/agency-settings";
import { FormField } from "components/form-field";
import { SingleImageUploadInput } from "components/inputs/single-image-upload-input";
import { accountBrandingModel } from "modules/agency-settings/models/account-branding-model";

type BrandingLogoProps = {
  size?: "normal" | "small";
  fieldName: string;
  heading: string;
  description: string;
  recommendationText: string;
  helpPopover: ReactNode;
};

export function BrandingLogo({
  heading,
  description,
  helpPopover,
  recommendationText,
  fieldName,
  size = "normal",
}: BrandingLogoProps) {
  // Upload image mutation
  const uploadImageMutation = useMutation({
    ...accountBrandingModel.mutations.uploadImage(),
  });

  // Handle image upload
  const handleImageUpload = async (file: File) => {
    const { data } = await uploadImageMutation.mutateAsync({
      file,
    });

    return data;
  };

  const transformImageResourceToPreviewUrl = (value: ImageResource) =>
    value.urls["log.primary"];

  return (
    <Flex direction={"column"} gap={2}>
      <Flex direction={"row"} align={"center"}>
        <Heading fontSize={"lg"} as={"h4"}>
          {heading}
        </Heading>
        {helpPopover}
      </Flex>
      <Text fontSize={"md"} color={"gray.600"}>
        {description}
      </Text>
      <Text fontSize={"md"} color={"gray.600"} mb={4}>
        {recommendationText}
      </Text>
      <FormField
        name={fieldName}
        label={null}
        Input={SingleImageUploadInput<ImageResource>}
        inputProps={{
          onImageUpload: handleImageUpload,
          transformValueToPreviewUrl: transformImageResourceToPreviewUrl,
          ...(size === "small" && {
            boxProps: {
              p: 2,

              overflow: "hidden",
              w: "150px",
              height: "150px",
              borderRadius: "100%",
            },
            placeholderProps: {
              fontSize: "sm",
            },
            imageProps: {
              aspectRatio: 1,
              borderRadius: "100%",
              objectFit: "cover",
            },
          }),
        }}
      />
    </Flex>
  );
}
